import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExtractionMismatch, SnbDocumentKind } from '@munifocus/contracts-gov';
import {
  Alert,
  AlertTitle,
  Box,
  Collapse,
  Icons,
  iconSize,
  palette,
  Stack,
  StatusChip,
  TagChip,
  Typography,
} from '@munifocus/ui';

/**
 * Which SNB record supplies which field, and where the two disagree.
 *
 * The intake form merges two documents into one flat set of inputs, so without
 * this an officer cannot tell what a second upload would add, or which record to
 * go back to when a value looks wrong. The grouping is fixed rather than derived
 * from what happened to be extracted: it has to be readable *before* either
 * document is uploaded, which is exactly when the officer is deciding what to
 * pull from SNB.
 *
 * Conflicts are shown here rather than only on the tombstone because this is the
 * screen where the wrong pair of documents gets uploaded — and a PID that is not
 * on the account means the two records describe different property.
 */

export interface SnbSourceGuideProps {
  /** Record kinds already read, so each group can say whether it has arrived. */
  extracted: SnbDocumentKind[];
  mismatches: ExtractionMismatch[];
}

/** Fields each record supplies, by the i18n key the form labels them with. */
const PAN_FIELDS = [
  'gov.file.pan',
  'gov.file.county',
  'gov.file.address',
  'gov.file.propertyDescription',
  'gov.file.taxingAuthority',
  'gov.file.taxClass',
  'gov.file.neighbourhood',
  'gov.file.harmonization',
  'gov.file.ownerName',
  'gov.file.ownerType',
  'gov.file.mailingAddress',
  'gov.file.postalCode',
];

const PARCEL_FIELDS = [
  'gov.parcel.parish',
  'gov.parcel.landTitlesStatus',
  'gov.parcel.landGazette',
  'gov.parcel.area',
  'gov.parcel.interestType',
  'gov.parcel.civicNumber',
  'gov.parcel.streetName',
  'gov.parcel.streetType',
  'gov.parcel.placeName',
];

export function SnbSourceGuide({ extracted, mismatches }: SnbSourceGuideProps) {
  const { t } = useTranslation();
  // Closed by default: it is a reference list of 21 field names, useful once
  // while deciding what to pull from SNB and pure noise on every later visit.
  const [open, setOpen] = useState(false);

  const group = (titleKey: string, kind: SnbDocumentKind, fieldKeys: string[]) => (
    <Box sx={{ flex: 1, minWidth: 240 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t(titleKey)}
        </Typography>
        <StatusChip
          status={extracted.includes(kind) ? 'success' : 'neutral'}
          label={
            extracted.includes(kind)
              ? t('gov.extraction.upload.done')
              : t('gov.extraction.notYetRead')
          }
        />
      </Stack>
      <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap' }}>
        {fieldKeys.map((key) => (
          <TagChip key={key} label={t(key)} />
        ))}
      </Stack>
    </Box>
  );

  return (
    <Box>
      {mismatches.length > 0 && (
        <Alert severity="warning" icon={<Icons.AlertTriangle size={iconSize.lg} />} sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>
            {t('gov.extraction.conflictTitle')}
          </AlertTitle>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {mismatches.map((m) => (
              <Typography key={m.field} variant="body2">
                <strong>{t(`gov.extraction.missing.${m.field}`, m.field)}</strong>
                {' — '}
                {t('gov.extraction.field.mismatchHint', {
                  pan: m.panValue ?? '—',
                  parcel: m.parcelValue ?? '—',
                })}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      <Box
        component="button"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          px: 1,
          py: 0.75,
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          textAlign: 'left',
          backgroundColor: 'transparent',
          transition: 'background-color 200ms',
          '&:hover': { backgroundColor: palette.brand.accentTint },
        }}
      >
        <Icons.Info size={iconSize.md} color={palette.text.muted} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: palette.text.primary }}>
          {t('gov.extraction.sourceLegend')}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Icons.ChevronDown
          size={iconSize.md}
          color={palette.text.muted}
          style={{
            flexShrink: 0,
            transition: 'transform 200ms',
            transform: open ? 'none' : 'rotate(-90deg)',
          }}
        />
      </Box>

      <Collapse in={open} timeout={200} unmountOnExit>
        <Box sx={{ px: 1, pt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {t('gov.extraction.sourceLegendHint')}
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
            {group('gov.extraction.fromPanGroup', 'snb_pan', PAN_FIELDS)}
            {group('gov.extraction.fromParcelGroup', 'snb_parcel', PARCEL_FIELDS)}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}
